import { Link } from "react-router";
import { HelpCircle, ChevronRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { faqCategories } from "../data/faqCategories";
import { Seo } from "../components/Seo";
import { JsonLd } from "../components/JsonLd";

const faqPageSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqCategories.flatMap((cat) =>
    cat.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    }))
  ),
};

export function FAQ() {
  return (
    <>
      <Seo
        title="FAQs"
        description="DeskReplay FAQ: plans, screen recording policies, storage, deployment, OIDC, AI features, insider threat, hybrid workforce, and regulated industry compliance."
        canonicalPath="/faq"
      />
      <JsonLd schema={faqPageSchema} id="faqpage" />
      <div className="bg-slate-950 min-h-screen">
      <section className="relative pt-36 pb-10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(13, 148, 136, 0.2) 0%, transparent 50%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center mb-6 lg:hidden">
            <div className="w-14 h-14 rounded-2xl bg-teal-500/15 border border-teal-500/25 flex items-center justify-center">
              <HelpCircle className="text-teal-400" size={28} />
            </div>
          </div>
          <div className="grid lg:grid-cols-[1fr_280px] gap-12 items-start">
            <div>
              <p className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-4">Help</p>
              <h1
                className="text-slate-100 mb-5"
                style={{
                  fontSize: "clamp(2rem, 4vw, 3rem)",
                  fontWeight: 800,
                  letterSpacing: "-0.03em",
                  lineHeight: "1.1",
                }}
              >
                FAQs
              </h1>
              <p className="text-on-dark-muted text-lg leading-relaxed max-w-3xl">
                Answers organized by topic: plans and licensing, admin roles, capture and storage, deployment, compliance,
                identity, and support. For contracts and legal wording, see our{" "}
                <Link to="/legal" className="text-teal-400 hover:text-teal-300 underline underline-offset-2">
                  policies
                </Link>
                . For packaged guides, visit{" "}
                <Link to="/resources" className="text-teal-400 hover:text-teal-300 underline underline-offset-2">
                  White Papers & Resources
                </Link>
                .
              </p>
            </div>
            <nav
              className="hidden lg:block rounded-2xl border border-slate-800 bg-slate-900/50 p-5 sticky top-28"
              aria-label="FAQ sections"
            >
              <p className="text-on-dark-dim text-xs uppercase tracking-widest font-semibold mb-3">Jump to</p>
              <ul className="flex flex-col gap-2">
                {faqCategories.map((cat) => (
                  <li key={cat.id}>
                    <a
                      href={`#${cat.id}`}
                      className="text-on-dark-muted hover:text-teal-400 text-sm transition-colors leading-snug block py-1"
                    >
                      {cat.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          <div className="mt-10 lg:hidden flex flex-wrap gap-2">
            {faqCategories.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                className="px-3 py-1.5 rounded-full border border-slate-700 text-on-dark-muted text-xs hover:border-teal-500/40 hover:text-teal-400 transition-colors"
              >
                {cat.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {faqCategories.map((category) => (
            <div key={category.id} id={category.id} className="scroll-mt-28">
              <h2 className="text-slate-100 text-xl sm:text-2xl font-bold tracking-tight mb-2">{category.title}</h2>
              {category.intro && <p className="text-on-dark-muted text-sm mb-6 max-w-3xl">{category.intro}</p>}
              <Accordion type="single" collapsible className="w-full rounded-2xl border border-slate-800 bg-slate-900/40 px-2 sm:px-5">
                {category.items.map((item, i) => (
                  <AccordionItem key={`${category.id}-${i}`} value={`${category.id}-${i}`} className="border-slate-800 px-2">
                    <AccordionTrigger className="text-slate-100 text-[15px] font-semibold hover:no-underline hover:text-teal-300 py-5 text-left [&>svg]:text-teal-500/80">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-on-dark-muted text-[15px] leading-relaxed pb-5">{item.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}

          <div className="rounded-2xl border border-teal-500/20 bg-teal-500/5 p-8 sm:p-10 text-center">
            <p className="text-slate-200 font-semibold text-lg mb-2">Still have questions?</p>
            <p className="text-on-dark-muted text-sm mb-6 max-w-xl mx-auto">
              Request an architecture session or{" "}
              <Link to="/pricing/compare" className="text-teal-400 hover:text-teal-300 underline underline-offset-2">
                compare plans
              </Link>{" "}
              with our team. For enterprise identity and self-hosted deployment, start with Enterprise pricing.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-500 text-white px-6 py-3 rounded-xl text-sm font-semibold transition-all hover:shadow-lg hover:shadow-teal-500/25"
              >
                Contact us
                <ChevronRight size={16} />
              </Link>
              <Link
                to="/pricing/compare"
                className="inline-flex items-center justify-center gap-2 border border-teal-500/35 text-teal-400 hover:bg-teal-500/10 px-6 py-3 rounded-xl text-sm font-semibold transition-colors"
              >
                Compare plans
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 border border-slate-700 text-on-dark-muted hover:text-teal-400 hover:border-teal-500/35 px-6 py-3 rounded-xl text-sm font-semibold transition-colors"
              >
                View pricing
              </Link>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
